import { BaseTable,  FooterMenu,  HeaderMenu, Loading, PaginationData, TableHeader } from "components"
import moment from "moment"
import { useState } from "react"
import { DashboardFillType, PaginationState } from "types"
import { convertNumber, default_pagination_value, exportPdf } from "utils"
import { filterByStatus, getTurnoverStatus, useInventoryTurnover } from "./utils/hooks/useInventoryTurnover"
import { FilterInventoryTurnOver } from "./components"
import { ExportExcelInventoryTurnover } from "./components/exports/ExportExcelInventoryTurnover"
import { ExportPdfInventoryTurnover } from "./components/exports/ExportPdfInventoryTurnover"

type FilterType = PaginationState & DashboardFillType & {
    category_id: string
    status: string
}

export function InventoryTurnoverPage() {
    const month = moment().format("MM")
    const year = moment().format("YYYY")
    const [fill, setFill] = useState<FilterType>({
        ...default_pagination_value,
        month,
        year,
        location_id: "",
        category_id: "",
        status: ""
    })

    const { data, isLoading, dropdownTurnover, pagination,fetchAll } = useInventoryTurnover({
        year_month: `${fill.year}${fill.month}`,
        location_id: fill.location_id,
        category_id: fill.category_id,
        page: fill.page,
        limit: fill.limit
    })

    const turnoverRules = data?.turnover_rule ?? [];

    const filteredData = filterByStatus(
        data?.data ?? [],
        turnoverRules,
        fill.status
    );

    return (
        <section className="container my-5 flex flex-col gap-3">
            <HeaderMenu title="INVENTORY TURNOVER" />

            <section className="flex flex-col gap-3">
                <section className="flex flex-col">
                    <FilterInventoryTurnOver
                        onChangeFilter={(res) => setFill({
                            ...fill,
                            month: moment(res.date_from).format("MM"),
                            year: moment(res.date_from).format("YYYY"),
                            location_id: res.location_id,
                            category_id: res.category_id?.toString() ?? "",
                            status: res.status?.toString() ?? ""
                        })}
                        dropdownTurnover={dropdownTurnover}
                    />
                    
                </section>

                <section className="flex flex-col mt-5">
                    <section className="grow">
                        <div className="max-h-[600px] overflow-y-auto">
                            <BaseTable>
                                <TableHeader>
                                    <tr>
                                        <th>CODE</th>
                                        <th>PRODUCT NAME</th>
                                        <th>BEGIN QTY</th>
                                        <th>END QTY</th>
                                        <th>AVG QTY</th>
                                        <th>UNIT SOLD</th>
                                        <th className="w-[50px] text-center">
                                            <div className="flex flex-col items-center leading-tight">
                                                <span>TURNOVER</span>
                                                <span className="text-xs">(QTY)</span>
                                            </div>
                                        </th>
                                        <th className="w-[50px] text-center">
                                            <div className="flex flex-col items-center leading-tight">
                                                <span>DIO</span>
                                                <span className="text-xs">(Days)</span>
                                            </div>
                                        </th>
                                        <th>STATUS</th>
                                    </tr>
                                </TableHeader>
                                <tbody>

                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={9} className="text-center">
                                                <Loading loading={isLoading} errorText="No data available" />
                                            </td>
                                        </tr>
                                    ) : filteredData.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center">
                                                <div className="text-center">No data available</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredData.map(item => {
                                            const rule = getTurnoverStatus(
                                                item.qty_turnover,
                                                turnoverRules
                                            );

                                            return (
                                                <tr key={item.product_id}>
                                                    <td className="text-left">{item?.product_barcode ?? "-"}</td>
                                                    <td className="text-left">{item?.product_name ?? "-"}</td>
                                                    <td className="text-right">{convertNumber(item?.begin_qty || 0)?.intoCurrency ?? "-"}</td>
                                                    <td className="text-right">{convertNumber(item?.end_qty || 0)?.intoCurrency ?? "-"}</td>
                                                    <td className="text-right">{convertNumber(item?.avg_qty || 0)?.intoCurrency ?? "-"}</td>
                                                    <td className="text-center">{convertNumber(item?.unit_sold || 0)?.intoCurrency ?? "-"}</td>
                                                    <td className="text-right">
                                                        {convertNumber(item?.qty_turnover || 0)?.intoCurrency ?? "-"}
                                                    </td>
                                                    <td className="text-right">
                                                        {item?.qty_turnover && item?.qty_turnover > 0 && item?.avg_qty
                                                            ? convertNumber(365 / Number(item.qty_turnover)).intoNormalAmount
                                                            : "0"}
                                                    </td>
                                                    <td>
                                                        {rule?.mr_product_turnover_name ?? "-"}
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </BaseTable>
                        </div>
                        <div className="mt-5">
                            <PaginationData
                                data={{
                                    pagination_data: pagination
                                }}
                                pagination={fill}
                                setPagination={setFill as React.Dispatch<React.SetStateAction<PaginationState>>}
                            />
                        </div>
                    </section>
                </section>

                <section className="mt-2">
                    <FooterMenu
                        export={{
                            actiontype: "export",
                            onClick: async () => {
                                const fullData = await fetchAll();
                            
                                const filteredFullData = filterByStatus(
                                    fullData.data ?? [],
                                    fullData.turnover_rule ?? [],
                                    fill.status
                                );
                            
                                ExportExcelInventoryTurnover({
                                    dataList: filteredFullData,
                                    turnoverRules: fullData.turnover_rule
                                });
                            }
                        }}
                        pdf={{
                            actiontype: "pdf",
                            onClick: async () => {
                                const fullData = await fetchAll();
                                
                                const filteredFullData = filterByStatus(
                                    fullData.data ?? [],
                                    fullData.turnover_rule ?? [],
                                    fill.status
                                );
                                
                                exportPdf({
                                    documentComponent: (
                                    <ExportPdfInventoryTurnover
                                        dataList={filteredFullData}
                                        turnoverRules={fullData.turnover_rule}
                                    />
                                    ),
                                    fileName: "Inventory_Turnover"
                                });
                            }
                        }}
                    />
                </section>
            </section>
        </section>
    )
}